/**
 * Get the full university name in the specified language
 * @param key - University key (e.g., 'hku', 'cuhk')
 * @param lang - Language code ('en' or 'zh')
 * @returns Full university name in the specified language
 */
export function getUniversityFullName(key: string | undefined, lang: string = 'en'): string {
  if (!key) return lang === 'zh' ? '未知大學' : 'Unknown University';
  
  const names: Record<string, { en: string; zh: string }> = {
    hku: { 
      en: "The University of Hong Kong", 
      zh: "香港大學" 
    },
    cuhk: { 
      en: "The Chinese University of Hong Kong", 
      zh: "香港中文大學" 
    },
    hkust: { 
      en: "The Hong Kong University of Science and Technology", 
      zh: "香港科技大學" 
    },
    cityu: { 
      en: "City University of Hong Kong", 
      zh: "香港城市大學" 
    },
    polyu: { 
      en: "The Hong Kong Polytechnic University", 
      zh: "香港理工大學" 
    },
    hkbu: { 
      en: "Hong Kong Baptist University", 
      zh: "香港浸會大學" 
    },
    lingnan: { 
      en: "Lingnan University", 
      zh: "嶺南大學" 
    },
    eduhk: { 
      en: "The Education University of Hong Kong", 
      zh: "香港教育大學" 
    },
    sssdp: {
      en: "SSSDP / TWC Programmes",
      zh: "統一收生方案"
    }
  };
  
  return names[key]?.[lang === 'zh' ? 'zh' : 'en'] || key.toUpperCase();
}

/**
 * Icon files under public/images/univ-icons are named by JUPAS institution
 * code ('6.png'), not by the lowercase university key the app passes around
 * ('hku'), so the key has to be translated before it can be used as a src.
 * Anything unmapped falls through unchanged, which covers the SSSDP prefixes
 * ('SA', 'SC', …) that are already stored under their own file names.
 */
const UNIVERSITY_ICON_FILES: Record<string, string> = {
  cityu: '1',
  hkbu: '2',
  polyu: '3',
  cuhk: '4',
  hkust: '5',
  hku: '6',
  lingnan: '7',
  eduhk: '8',
  hkmu: '9',
  sssdp: 'sssdp',
};

export function getUniversityIconSrc(key: string | undefined): string {
  const normalized = (key ?? '').toLowerCase();
  return `/images/univ-icons/${UNIVERSITY_ICON_FILES[normalized] ?? key ?? ''}.png`;
}
